// feature/invitations-email — Admin · Users + pending invitations.
// Two tabs: "Usuarios" (active accounts) and "Pendientes" (invitations).
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { users as usersApi, invitations as invApi, specializations as specsApi } from "../../api/endpoints";
import { ALL_SPECIALIZATIONS, SPECIALIZATION_LABEL, PRIVILEGED_SPECIALIZATIONS } from "../../utils/permissions";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import Modal from "../../components/Modal";
import Confirm from "../../components/Confirm";
import { useToast } from "../../components/Toast";
import { timeAgo } from "../../components/timeAgo";
import { useAuth } from "../../auth/AuthContext";

const ROLE_OPTS = [
  ["admin",            "Administrador"],
  ["reparto",          "Reparto / Auditoría"],
  ["insumos",          "Insumos"],
  ["reparto_insumos",  "Reparto de insumos"],
  ["revisor_fallos",   "Revisor de fallos"],
  ["abogado",          "Abogado"],
  ["auditor",          "Auditor"],
  ["directora",        "Directora — supervisión operativa"],
];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTS);

// feature/multi-role-admin-ui. Renders one badge per role on a
// user row. Falls back to the legacy single u.role when u.roles[] is empty so
// admins can still see something while the user_roles backfill catches up.
function RoleBadges({ user }) {
  const roles = (user.roles && user.roles.length) ? user.roles : (user.role ? [user.role] : []);
  if (!roles.length) return <span className="muted">—</span>;
  return (
    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
      {roles.map((r) => (
        <span key={r} className="badge mint">{ROLE_LABEL[r] || r}</span>
      ))}
    </span>
  );
}
const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";
const ALLOWED_DOMAIN = "@edys-sas.com";

export default function Users() {
  const [tab, setTab] = useState("active");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="row">
        <h1 style={{ margin: 0 }}>Usuarios</h1>
        <div style={{ flex: 1 }} />
        <button onClick={() => setInviteOpen(true)}>+ Invitar usuario</button>
      </div>

      <div className="row" style={{ gap: 4, borderBottom: "1px solid var(--border-hairline)" }}>
        <Tab label="Usuarios"   active={tab === "active"}  onClick={() => setTab("active")} />
        <Tab label="Equipo de insumos" active={tab === "team"} onClick={() => setTab("team")} />
        <Tab label="Pendientes" active={tab === "pending"} onClick={() => setTab("pending")} />
      </div>

      {tab === "active"  && <ActiveUsersTab key={`a-${refreshTick}`} />}
      {tab === "team"    && <InsumosTeamTab key={`t-${refreshTick}`} />}
      {tab === "pending" && <PendingTab    key={`p-${refreshTick}`} />}

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => { setInviteOpen(false); setTab("pending"); setRefreshTick(t => t + 1); }}
      />
    </div>
  );
}

function Tab({ label, active, onClick }) {
  return (
    <div onClick={onClick} style={{
      padding: "10px 14px", cursor: "pointer", fontSize: 13,
      color: active ? "var(--accent-mint)" : "var(--text-muted)",
      borderBottom: "2px solid " + (active ? "var(--accent-mint)" : "transparent"),
      fontWeight: active ? 500 : 400, marginBottom: -1,
    }}>{label}</div>
  );
}

// ===========================================================================
// ACTIVE USERS TAB (existing user roster, reorder + toggle + reset password)
// ===========================================================================
function ActiveUsersTab() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetReveal, setResetReveal] = useState(null);

  function load() {
    setData(null);
    usersApi.list().then(r => setData(r.data)).catch(() => setData([]));
  }
  useEffect(() => { load(); }, []);

  // feature/single-role-audit — FALSE POSITIVE: insumos team management
  // intentionally filters by PRIMARY role since insumos_order is a
  // primary-insumos concept (the rotation queue). Users with insumos
  // as a secondary role (e.g. Rosero abogado+carpetas+insumos) are
  // intentionally excluded from the rotation order UI.
  const insumosMembers = useMemo(() => (data || [])
    .filter(u => u.role === "insumos")
    .sort((a, b) => (a.insumos_order ?? 999) - (b.insumos_order ?? 999)), [data]);

  async function moveInsumos(u, dir) {
    const ordered = insumosMembers.slice();
    const idx = ordered.findIndex(x => x.id === u.id);
    if (idx < 0) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= ordered.length) return;
    const a = ordered[idx], b = ordered[swap];
    try {
      await usersApi.update(b.id, { insumos_order: 9999 });
      await usersApi.update(a.id, { insumos_order: b.insumos_order });
      await usersApi.update(b.id, { insumos_order: a.insumos_order });
      toast.push("Orden actualizado");
      load();
    } catch { toast.push("Error al reordenar", "error"); }
  }

  async function toggleActive(u) {
    try {
      await usersApi.toggleActive(u.id);
      toast.push(`Usuario ${u.active ? "desactivado" : "activado"}`);
      setConfirmToggle(null); load();
    } catch { toast.push("Error", "error"); }
  }

  async function autoResetPwd(u) {
    try {
      const r = await usersApi.autoResetPassword(u.id);
      setResetTarget(null); setResetReveal({ user: u, password: r.data.password });
    } catch { toast.push("Error al restablecer", "error"); }
  }

  return (
    <>
      <div className="card" style={{ padding: 0 }}>
        {data == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !data.length ? (
          <Empty title="Sin usuarios activos" />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Nombre</th><th>Identificador</th><th>Rol</th>
                <th>Orden insumos</th><th>Activo</th><th>Último acceso</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.map(u => {
                // FALSE POSITIVE: PRIMARY-role check for the insumos
                // rotation column display; same scope as insumosMembers.
                const isInsumos = u.role === "insumos";
                const myIdx = isInsumos ? insumosMembers.findIndex(x => x.id === u.id) : -1;
                return (
                  <tr key={u.id}>
                    <td><strong>{u.name}</strong></td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {u.username ? <span><strong style={{ color: "var(--text-primary)" }}>{u.username}</strong> · {u.email}</span> : u.email}
                    </td>
                    <td><RoleBadges user={u} /></td>
                    <td>
                      {isInsumos ? (
                        <div className="row" style={{ gap: 4 }}>
                          <span style={{ minWidth: 18, fontWeight: 500 }}>{u.insumos_order ?? "—"}</span>
                          <button className="ghost sm" disabled={myIdx === 0} onClick={() => moveInsumos(u, -1)} title="Subir">↑</button>
                          <button className="ghost sm" disabled={myIdx === insumosMembers.length - 1} onClick={() => moveInsumos(u, +1)} title="Bajar">↓</button>
                        </div>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td>
                      <span className={"badge " + (u.active ? "success" : "danger")}>
                        {u.active ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>{fmt(u.last_login)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="ghost sm" onClick={() => setEditing(u)}>Editar</button>
                      <button className="ghost sm" onClick={() => setResetTarget(u)}>Restablecer contraseña</button>
                      <button className="ghost sm" onClick={() => setConfirmToggle(u)}>{u.active ? "Desactivar" : "Activar"}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <EditUserModal target={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />

      <Confirm
        open={!!confirmToggle}
        title={confirmToggle?.active ? "Desactivar usuario" : "Activar usuario"}
        message={confirmToggle ? `¿${confirmToggle.active ? "Desactivar" : "Activar"} a ${confirmToggle.name}?` : ""}
        onClose={() => setConfirmToggle(null)}
        onConfirm={() => toggleActive(confirmToggle)}
        confirmText={confirmToggle?.active ? "Desactivar" : "Activar"}
        danger={confirmToggle?.active}
      />

      <Confirm
        open={!!resetTarget}
        title="Restablecer contraseña"
        message={resetTarget ? `Se generará una nueva contraseña para ${resetTarget.name}. Esta es la única forma de verla — no se mostrará después.` : ""}
        onClose={() => setResetTarget(null)}
        onConfirm={() => autoResetPwd(resetTarget)}
        confirmText="Generar"
      />

      <RevealPassword target={resetReveal} onClose={() => setResetReveal(null)} />
    </>
  );
}

// ===========================================================================
// PENDING INVITATIONS TAB
// ===========================================================================
function PendingTab() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);

  function load() { setData(null); invApi.list().then(r => setData(r.data)).catch(() => setData([])); }
  useEffect(() => { load(); }, []);

  async function resend(inv) {
    try { await invApi.resend(inv.id); toast.push(`Invitación reenviada a ${inv.email}`); load(); }
    catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
  }
  async function cancel() {
    try {
      await invApi.cancel(confirmCancel.id);
      toast.push("Invitación cancelada");
      setConfirmCancel(null); load();
    } catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
  }

  return (
    <>
      <div className="card" style={{ padding: 0 }}>
        {data == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !data.length ? (
          <Empty title="No hay invitaciones pendientes" message="Cuando invite a alguien, aparecerá aquí hasta que active su cuenta." />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Correo</th><th>Nombre</th><th>Rol</th>
                <th>Invitado por</th><th>Enviado</th><th>Expira</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.map(inv => (
                <tr key={inv.id}>
                  <td><strong>{inv.email}</strong></td>
                  <td>{inv.name}</td>
                  <td><span className="badge mint">{ROLE_LABEL[inv.role] || inv.role}</span></td>
                  <td>{inv.invited_by_name || "—"}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{timeAgo(inv.created_at)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{fmt(inv.expires_at)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="ghost sm" onClick={() => resend(inv)}>Reenviar</button>
                    <button className="ghost sm" style={{ color: "var(--color-danger)" }} onClick={() => setConfirmCancel(inv)}>Cancelar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Confirm
        open={!!confirmCancel}
        title="Cancelar invitación"
        message={confirmCancel ? `La invitación enviada a ${confirmCancel.email} dejará de ser válida.` : ""}
        onClose={() => setConfirmCancel(null)}
        onConfirm={cancel}
        confirmText="Cancelar invitación"
        danger
      />
    </>
  );
}

// ===========================================================================
// INVITE MODAL
// ===========================================================================
function InviteModal({ open, onClose, onCreated }) {
  const toast = useToast();
  const [name, setName]   = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole]   = useState("abogado");
  const [busy, setBusy]   = useState(false);
  const [emailErr, setEmailErr] = useState(null);

  useEffect(() => {
    if (open) { setName(""); setEmail(""); setRole("abogado"); setEmailErr(null); }
  }, [open]);

  function validateEmail(v) {
    const cleaned = (v || "").trim().toLowerCase();
    if (!cleaned) return null;
    if (!cleaned.endsWith(ALLOWED_DOMAIN)) {
      return `El correo debe terminar en ${ALLOWED_DOMAIN}.`;
    }
    return null;
  }

  async function submit(e) {
    e.preventDefault();
    const err = validateEmail(email);
    if (err) { setEmailErr(err); return; }
    setBusy(true);
    try {
      await invApi.create({ name: name.trim(), email: email.trim().toLowerCase(), role });
      toast.push(`Invitación enviada a ${email.trim().toLowerCase()}`);
      onCreated?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al enviar la invitación", "error");
    } finally { setBusy(false); }
  }

  return (
    <Modal
      open={open}
      title="Invitar nuevo usuario"
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <button className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
          <button disabled={busy} onClick={submit}>
            {busy && <Spinner color="currentColor" />} Enviar invitación
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="col" style={{ gap: 12 }}>
        <div>
          <label>Nombre</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Nombre completo" />
        </div>
        <div>
          <label>Correo</label>
          <input type="email" value={email}
                 onChange={(e) => { setEmail(e.target.value); setEmailErr(null); }}
                 onBlur={(e) => setEmailErr(validateEmail(e.target.value))}
                 required placeholder={`usuario${ALLOWED_DOMAIN}`} />
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            Solo se permiten correos del dominio <strong>{ALLOWED_DOMAIN}</strong>.
          </div>
          {emailErr && <div style={{ color: "var(--color-danger)", fontSize: 12, marginTop: 4 }}>{emailErr}</div>}
        </div>
        <div>
          <label>Rol</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </form>
    </Modal>
  );
}

// ===========================================================================
// EDIT EXISTING USER MODAL  (kept simple — admin can flip role/active here)
// ===========================================================================
// feature/multi-role-admin-ui.
// Roles are managed as a checkbox grid (a user can hold multiple roles). The
// primary `users.role` becomes the first selected role; all selected roles are
// persisted to user_roles atomically by PATCH /api/users/:id/roles.
function EditUserModal({ target, onClose, onSaved }) {
  const toast = useToast();
  // feature/role-update-self-aware — when an admin edits their own user, swap
  // in the freshly-issued JWT (so backend permission checks pick up the new
  // roles immediately) and refresh AuthContext (so sidebar/topbar/route guards
  // re-render with the new role set without forcing a logout).
  const { user: currentUser, refreshUser } = useAuth();
  const [name, setName] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [insumosOrder, setInsumosOrder] = useState("");
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  // feature/lawyer-specializations.
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [initialSpecs, setInitialSpecs] = useState([]);

  useEffect(() => {
    if (!target) return;
    setName(target.name);
    const initial = (target.roles && target.roles.length)
      ? target.roles
      : (target.role ? [target.role] : []);
    setSelectedRoles(initial);
    setInsumosOrder(target.insumos_order ?? "");
    setActive(target.active);
    // Load current specializations for this user.
    specsApi.list(target.id)
      .then((r) => {
        const specs = r.data?.specializations || [];
        setSelectedSpecs(specs);
        setInitialSpecs(specs);
      })
      .catch(() => { setSelectedSpecs([]); setInitialSpecs([]); });
  }, [target]);

  function toggleRole(key) {
    setSelectedRoles((prev) =>
      prev.includes(key) ? prev.filter((r) => r !== key) : [...prev, key]
    );
  }

  function toggleSpec(key) {
    setSelectedSpecs((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]
    );
  }

  async function save(e) {
    e.preventDefault();
    if (selectedRoles.length === 0) {
      toast.push("Seleccione al menos un rol", "error");
      return;
    }
    setBusy(true);
    try {
      // Two-step: update name/active/insumos_order first, then atomically
      // replace the user_roles set. The /roles endpoint also pins
      // users.role to the first item in selectedRoles as the display role.
      const isInsumos = selectedRoles.includes("insumos");
      const updateRes = await usersApi.update(target.id, {
        name,
        role: selectedRoles[0],
        active,
        insumos_order: isInsumos && insumosOrder !== "" ? Number(insumosOrder) : null,
      });
      // Only call setRoles if the role set actually changed — this avoids
      // unnecessary user_roles churn.
      const initial = (target.roles && target.roles.length)
        ? [...target.roles].sort()
        : (target.role ? [target.role] : []);
      const next = [...selectedRoles].sort();
      const changed = initial.length !== next.length
        || initial.some((r, i) => r !== next[i]);
      let rolesRes = null;
      if (changed) {
        rolesRes = await usersApi.setRoles(target.id, selectedRoles);
      }

      // feature/lawyer-specializations. Persist specializations
      // only if abogado is in the role set and the spec list changed.
      if (selectedRoles.includes("abogado")) {
        const specsBefore = [...initialSpecs].sort();
        const specsAfter  = [...selectedSpecs].sort();
        const specsChanged = specsBefore.length !== specsAfter.length
          || specsBefore.some((s, i) => s !== specsAfter[i]);
        if (specsChanged) {
          await specsApi.set(target.id, selectedSpecs);
        }
      } else if (initialSpecs.length > 0) {
        // Lawyer role removed — clear specs to keep things consistent.
        await specsApi.set(target.id, []);
      }

      // feature/role-update-self-aware — if the actor edited themselves,
      // swap in the new token (issued by either endpoint) and re-fetch
      // /me so AuthContext (and everything that reads from it: sidebar,
      // topbar, route guards, can() helper) picks up the new roles.
      const isSelfEdit = currentUser && currentUser.id === target.id;
      if (isSelfEdit) {
        const newToken = rolesRes?.data?.token || updateRes?.data?.token;
        if (newToken) {
          try { localStorage.setItem("eds.token", newToken); } catch (_) {}
        }
        try { await refreshUser?.(); } catch (_) {}
      }

      toast.push("Usuario actualizado");
      onSaved?.();
    } catch (err) {
      toast.push(err?.response?.data?.error || "Error al guardar", "error");
    } finally { setBusy(false); }
  }

  const isInsumos = selectedRoles.includes("insumos");

  return (
    <Modal
      open={!!target}
      title={target ? `Editar — ${target.name}` : ""}
      onClose={busy ? undefined : onClose}
      footer={<>
        <button className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
        <button disabled={busy} onClick={save}>{busy && <Spinner color="currentColor" />} Guardar</button>
      </>}
    >
      {target && (
        <form onSubmit={save} className="col" style={{ gap: 12 }}>
          <div>
            <label>Nombre</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <label>Roles</label>
            <div className="muted" style={{ fontSize: 11, marginTop: 2, marginBottom: 8 }}>
              Un usuario puede tener varios roles. El primer rol seleccionado se
              usa como rol principal en la interfaz.
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 8,
            }}>
              {ROLE_OPTS.map(([key, label]) => (
                <label key={key} style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "8px 12px",
                  border: `1px solid ${selectedRoles.includes(key) ? "var(--accent-mint)" : "var(--border-hairline)"}`,
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: 13,
                  background: selectedRoles.includes(key) ? "var(--accent-mint-bg)" : "transparent",
                  textTransform: "none", letterSpacing: 0, color: "var(--text-body)", marginBottom: 0,
                }}>
                  <input type="checkbox"
                         checked={selectedRoles.includes(key)}
                         onChange={() => toggleRole(key)}
                         style={{ margin: 0 }} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            {selectedRoles.length === 0 && (
              <div style={{ color: "var(--color-danger)", fontSize: 12, marginTop: 6 }}>
                Seleccione al menos un rol
              </div>
            )}
          </div>

          {isInsumos && (
            <div>
              <label>Orden insumos</label>
              <input type="number" min="1" value={insumosOrder}
                     onChange={(e) => setInsumosOrder(e.target.value)} />
            </div>
          )}

          {/* feature/lawyer-specializations. */}
          {selectedRoles.includes("abogado") && (
            <div>
              <label>Especializaciones</label>
              <div className="muted" style={{ fontSize: 11, marginTop: 2, marginBottom: 8 }}>
                Etiquetas privilegiadas (sanciones, impugnaciones, memoriales) permiten marcar
                contestada directamente. Las demás (tutelas, desacatos, fallos_contestacion)
                requieren pasar por moderación si están bloqueadas.
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 8,
              }}>
                {ALL_SPECIALIZATIONS.map((key) => {
                  const isPrivileged = PRIVILEGED_SPECIALIZATIONS.includes(key);
                  const checked = selectedSpecs.includes(key);
                  return (
                    <label key={key} style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "8px 12px",
                      border: `1px solid ${checked ? (isPrivileged ? "var(--accent-mint)" : "var(--border-hairline)") : "var(--border-hairline)"}`,
                      borderRadius: 4,
                      cursor: "pointer",
                      fontSize: 13,
                      background: checked
                        ? (isPrivileged ? "var(--accent-mint-bg)" : "var(--fill-1)")
                        : "transparent",
                      textTransform: "none", letterSpacing: 0, color: "var(--text-body)", marginBottom: 0,
                    }}>
                      <input type="checkbox"
                             checked={checked}
                             onChange={() => toggleSpec(key)}
                             style={{ margin: 0 }} />
                      <span>
                        {SPECIALIZATION_LABEL[key]}
                        {isPrivileged && (
                          <span className="muted" style={{ fontSize: 10, marginLeft: 4 }}>(privilegiada)</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", fontSize: 13, color: "var(--text-body)", letterSpacing: 0 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Cuenta activa
          </label>
        </form>
      )}
    </Modal>
  );
}

function RevealPassword({ target, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!target) return null;
  const { user, password } = target;
  return (
    <Modal
      open={!!target}
      title="Contraseña generada"
      onClose={onClose}
      footer={<button onClick={onClose}>He guardado la contraseña</button>}
    >
      <p>Comparta esta contraseña con <strong>{user.name}</strong> ({user.email}) por un canal seguro.</p>
      <div className="card tight" style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
        <code style={{ flex: 1, fontSize: 16, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{password}</code>
        <button className="secondary sm" onClick={async () => {
          try { await navigator.clipboard.writeText(password); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
        }}>{copied ? "Copiado ✓" : "Copiar"}</button>
      </div>
      <div className="warn-inline" style={{ marginTop: 12 }}>
        Esta contraseña no se mostrará de nuevo. Asegúrese de copiarla antes de cerrar.
      </div>
    </Modal>
  );
}

// =====================================================================
// feature/insumos-auto-assignment — Live Insumos team ops view (admin only).
// =====================================================================
function InsumosTeamTab() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});      // { userId: { daily_quota, available_for_assignment } }
  const [savingId, setSavingId] = useState(null);

  function load() {
    usersApi.withRoles().then(r => setData(r.data || [])).catch(() => setData([]));
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 30_000); // 30s auto-refresh
    return () => clearInterval(t);
  }, []);

  // Show insumos members (matched by primary role OR roles[]).
  const team = useMemo(() => (data || []).filter(u =>
    u.role === "insumos" || (u.roles || []).includes("insumos")
  ), [data]);

  const summary = useMemo(() => {
    const available = team.filter(u => u.available_for_assignment).length;
    const total = team.length;
    const todayTotal = team.reduce((s, u) => s + (Number(u.cases_assigned_today_actual) || 0), 0);
    const quotaTotal = team.filter(u => u.available_for_assignment).reduce((s, u) => s + (Number(u.daily_quota) || 0), 0);
    const onlineNow = team.filter(u => u.is_online).length;
    return { available, absent: total - available, todayTotal, quotaTotal, onlineNow };
  }, [team]);

  function setDraft(userId, patch) {
    setDrafts(d => ({ ...d, [userId]: { ...d[userId], ...patch } }));
  }
  function effective(u, key) {
    const d = drafts[u.id] || {};
    return key in d ? d[key] : u[key];
  }
  function isDirty(u) {
    const d = drafts[u.id]; if (!d) return false;
    return ("daily_quota" in d && d.daily_quota !== u.daily_quota)
        || ("available_for_assignment" in d && d.available_for_assignment !== u.available_for_assignment);
  }
  async function save(u) {
    setSavingId(u.id);
    try {
      const body = {};
      const d = drafts[u.id] || {};
      if ("daily_quota" in d) body.daily_quota = Number(d.daily_quota);
      if ("available_for_assignment" in d) body.available_for_assignment = !!d.available_for_assignment;
      await usersApi.setInsumosConfig(u.id, body);
      toast.push("Configuración actualizada");
      setDrafts(prev => { const n = { ...prev }; delete n[u.id]; return n; });
      load();
    } catch (e) { toast.push(e?.response?.data?.error || "Error", "error"); }
    finally { setSavingId(null); }
  }

  if (data == null) return <div className="card" style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>;

  return (
    <>
      <div className="glass-section" style={{ marginBottom: 16 }}>
        <h3>Equipo de insumos hoy</h3>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
          <li>{summary.available} disponibles · {summary.absent} ausentes</li>
          <li>{summary.todayTotal} casos asignados de {summary.quotaTotal} cuota total {summary.quotaTotal ? `(${Math.round(100 * summary.todayTotal / summary.quotaTotal)}%)` : ""}</li>
          <li>{summary.onlineNow} usuarios en línea ahora</li>
        </ul>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {!team.length ? <Empty title="Sin usuarios de insumos" /> : (
          <table>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Estado</th>
                <th>Hoy / cuota</th>
                <th>Activos</th>
                <th>Disponible</th>
                <th>Cuota</th>
                <th>Última actividad</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {team.map(u => {
                const today = Number(u.cases_assigned_today_actual) || 0;
                const quota = Number(u.daily_quota) || 0;
                const pct = quota ? Math.min(100, Math.round(100 * today / quota)) : 0;
                const over = quota > 0 && today >= quota;
                const dirty = isDirty(u);
                return (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {u.is_online && <span className="user-online-dot" title="En línea (últimos 5 min)" />}
                        <strong>{u.name}</strong>
                      </div>
                      <div className="muted" style={{ fontSize: 11 }}>{u.email}</div>
                    </td>
                    <td>{u.active
                      ? <span className="badge success">Activo</span>
                      : <span className="badge danger">Inactivo</span>}</td>
                    <td>
                      <div style={{ fontSize: 13 }}>
                        <strong>{today}</strong> / {quota}
                      </div>
                      <div className="user-quota-bar" aria-hidden="true">
                        <div className={"user-quota-fill" + (over ? " over-quota" : "")} style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                    <td>{u.total_active_cases ?? 0}</td>
                    <td>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={!!effective(u, "available_for_assignment")}
                          onChange={(e) => setDraft(u.id, { available_for_assignment: e.target.checked })}
                        />
                        {effective(u, "available_for_assignment") ? "Sí" : "No"}
                      </label>
                    </td>
                    <td>
                      <input
                        type="number" min="0" max="1000"
                        value={effective(u, "daily_quota") ?? ""}
                        onChange={(e) => setDraft(u.id, { daily_quota: e.target.value })}
                        style={{ width: 70 }}
                      />
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>{u.last_activity_at ? timeAgo(u.last_activity_at) : "—"}</td>
                    <td style={{ whiteSpace: "nowrap", display: "flex", gap: 4 }}>
                      {dirty && (
                        <button className="sm" disabled={savingId === u.id} onClick={() => save(u)}>
                          {savingId === u.id ? "Guardando…" : "Guardar"}
                        </button>
                      )}
                      <Link to={`/admin/users/${u.id}/casos`}>
                        <button className="ghost sm" title="Ver casos asignados">Ver casos →</button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
